/**
 * The company a page is pointed at, when the URL names one at all.
 *
 * Only super users ever browse another company, and they reach it by naming the company in the
 * URL — the JWT's company_id is always their own (see `get_performyard_jwt`). Two sources, in
 * order:
 *
 *   1. An explicit `companyId` query param — the convention the admin settings home page uses.
 *   2. A `/super-user-admin/` origin carried in `from`, whose first object id is the company.
 *      The id sits at different depths across those routes (`/super-user-admin/<companyId>`,
 *      `/super-user-admin/employees/<companyId>/`, `/super-user-admin/reviews/forms/<companyId>`),
 *      so the position is not pinned. Employee links are built by shared helpers across dozens of
 *      call sites that know nothing about tenancy, so the super user origin is what is actually
 *      available on the employee details page.
 *
 * Used to *refuse* cross-company rendering, not to scope a read: employee-scoped pages are
 * single-tenant by design, and a super user who wants an employee's data takes over their
 * account instead (PY-13817). Compare the result against the caller's own company before
 * treating a page as cross-company, since a super user can also name their own.
 */
import { getSanitizedFromEvenInFreshTab, nestedFromChain } from "./breadcrumbOrigin";

const OBJECT_ID = /^[0-9a-f]{24}$/i;
const SUPER_USER_COMPANY_ORIGIN = /^\/super-user-admin\/(?:[^/]+\/)*?([0-9a-f]{24})(?:[/?#]|$)/i;

/**
 * Normalizes to the lowercase form the JWT carries, so a hand-typed uppercase id still compares
 * equal to the caller's own company, and refuses anything that is not an object id at all.
 */
const asCompanyId = (value?: string | null): string | undefined =>
	value && OBJECT_ID.test(value) ? value.toLowerCase() : undefined;

/** The target company named by one `from` hop, by super user path or by its own `companyId`. */
const companyFromOrigin = (origin: string): string | undefined => {
	const fromPath = asCompanyId(SUPER_USER_COMPANY_ORIGIN.exec(origin)?.[1]);
	if (fromPath) return fromPath;

	const queryIndex = origin.indexOf("?");
	if (queryIndex === -1) return undefined;
	return asCompanyId(new URLSearchParams(origin.slice(queryIndex)).get("companyId"));
};

// Ungated on purpose: `getSanitizedFrom` drops `from` for fresh-tab loads so breadcrumbs anchor
// on Dashboard, but opening an employee in a new tab still has to be recognized as cross-company.
const FROM_OPTIONS = { evenInFreshTab: true } as const;

export const readTargetCompanyId = (search: string = window.location.search): string | undefined => {
	const explicit = asCompanyId(new URLSearchParams(search).get("companyId"));
	if (explicit) return explicit;

	const outer = getSanitizedFromEvenInFreshTab(search);
	if (!outer) return undefined;

	// Walk the nested hops too: clicking a manager's name on a details page re-anchors `from`
	// on that details page, burying the super user origin one level deeper each time.
	for (const origin of [outer, ...nestedFromChain(outer, FROM_OPTIONS)]) {
		const companyId = companyFromOrigin(origin);
		if (companyId) return companyId;
	}

	return undefined;
};
